import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import "./scss/Settings.scss";

// Custom Components
import ProfileNavbar from "./ProfileNavbar";
import SearchSelect from "../Global/SearchSelect";
import AutoSaveStatus from "../Global/AutoSaveStatus";

// Redux Actions & Utilities
import { showError } from "../../utils/snackbarHelpers";
import { fetchUser } from "../../redux/Slices/thunks";
import { fetchWorkspaces } from "../../redux/Slices/workspaceSlice";

// Config & Helpers
import { config } from "../../config";
import { textTransform, find } from "../../utils/helpers";

// Custom Hooks & Context
import { useTheme } from "../../context/ThemeContext";
import { showSnackBar } from "../../redux/Slices/snackbarSlice";

const Settings = () => {
    // Redux Hooks
    const dispatch = useDispatch();
    const { shortId } = useParams(); // Get workspace shortId from URL

    // Theme Context
    const { setTheme } = useTheme();

    // Redux State
    const { workspaces } = useSelector(state => state.workspaces);
    const { user } = useSelector(state => state.user);

    // State Management
    const [settings, setSettings] = useState(null);
    const [slackSettings, setSlackSettings] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [autoSaving, setAutoSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    // Workspace Settings
    const [currentWorkspace, setCurrentWorkspace] = useState(null);

    // Appearance Settings
    const [selectedThemeColor, setSelectedThemeColor] = useState("blue");
    const [selectedTheme, setSelectedTheme] = useState("system");
    const [highContrast, setHighContrast] = useState(false);

    // Localization Settings
    const [language, setLanguage] = useState("english");
    const [region, setRegion] = useState("Europe/London");

    // Notification Settings
    const [pushNotifications, setPushNotifications] = useState(false);
    const [slackNotifications, setSlackNotifications] = useState(false);
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [slackConnected, setSlackConnected] = useState(null);
    const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

    // Check if we're in workspace context
    const isWorkspaceSettings = Boolean(shortId);



    // Options Data
    const languageOptions = [
        { value: "en", label: "English" },
        { value: "es", label: "Español" },
        { value: "zh", label: "中文 (Mandarin)" },
        { value: "ar", label: "العربية" },
        { value: "fr", label: "Français" },
        { value: "ru", label: "Русский" },
        { value: "pt", label: "Português" },
        { value: "de", label: "Deutsch" },
        { value: "ja", label: "日本語" },
        { value: "it", label: "Italiano" },
        { value: "ko", label: "한국어" },
        { value: "tr", label: "Türkçe" },
    ];

    const regionOptions = [
        { value: "Pacific/Honolulu", label: "(GMT-10:00) US/Hawaii (HST)" },
        { value: "America/Anchorage", label: "(GMT-09:00) US/Alaska (AKST)" },
        { value: "America/Los_Angeles", label: "(GMT-08:00) US/Pacific (PST)" },
        { value: "America/Denver", label: "(GMT-07:00) US/Mountain (MST)" },
        { value: "America/Chicago", label: "(GMT-06:00) US/Central (CST)" },
        { value: "America/New_York", label: "(GMT-05:00) US/Eastern (EST)" },
        { value: "America/Toronto", label: "(GMT-05:00) Canada/Toronto (EST)" },
        { value: "America/Sao_Paulo", label: "(GMT-03:00) Brazil/Sao Paulo (BRT)" },
        { value: "Atlantic/Azores", label: "(GMT-01:00) Azores (AZOT)" },
        { value: "Europe/London", label: "(GMT+00:00) UK/London (GMT)" },
        { value: "Europe/Berlin", label: "(GMT+01:00) Germany/Berlin (CET)" },
        { value: "Europe/Paris", label: "(GMT+01:00) France/Paris (CET)" },
        { value: "Europe/Athens", label: "(GMT+02:00) Greece/Athens (EET)" },
        { value: "Asia/Dubai", label: "(GMT+04:00) UAE/Dubai (GST)" },
        { value: "Asia/Kolkata", label: "(GMT+05:30) India/Kolkata (IST)" },
        { value: "Asia/Dhaka", label: "(GMT+06:00) Bangladesh/Dhaka (BST)" },
        { value: "Asia/Bangkok", label: "(GMT+07:00) Thailand/Bangkok (ICT)" },
        { value: "Asia/Shanghai", label: "(GMT+08:00) China/Shanghai (CST)" },
        { value: "Asia/Tokyo", label: "(GMT+09:00) Japan/Tokyo (JST)" },
        { value: "Australia/Sydney", label: "(GMT+11:00) Australia/Sydney (AEDT)" },
    ];

    const themeColors = [
        { name: "blue", code: "#0966ff" },
        { name: "purple", code: "#7f77f1" },
        { name: "sky-blue", code: "#1090e0" },
        { name: "pink", code: "#ee5e99" },
        { name: "violet", code: "#b660e0" },
        { name: "light-blue", code: "#6985ff" },
        { name: "orange", code: "#e16b16" },
        { name: "teal", code: "#0f9d9f" },
        { name: "brown", code: "#aa8d80" },
        { name: "green", code: "#3cb88b" },
    ];

    // Helper function to map backend language to frontend language code
    const mapBackendLanguageToCode = (backendLanguage) => {
        const languageMap = {
            'english': 'en',
            'spanish': 'es',
            'chinese': 'zh',
            'arabic': 'ar',
            'french': 'fr',
            'russian': 'ru',
            'portuguese': 'pt',
            'german': 'de',
            'japanese': 'ja',
            'italian': 'it',
            'korean': 'ko',
            'turkish': 'tr'
        };
        return languageMap[backendLanguage?.toLowerCase()] || backendLanguage || 'en';
    };

    // Helper function to map frontend language code to backend language
    const mapLanguageCodeToBackend = (languageCode) => {
        const codeMap = {
            'en': 'english',
            'es': 'spanish',
            'zh': 'chinese',
            'ar': 'arabic',
            'fr': 'french',
            'ru': 'russian',
            'pt': 'portuguese',
            'de': 'german',
            'ja': 'japanese',
            'it': 'italian',
            'ko': 'korean',
            'tr': 'turkish'
        };
        return codeMap[languageCode] || languageCode;
    };

    // Auto-save function - save only the changed setting
    const autoSave = useCallback(async (changedSetting) => {
        try {
            setAutoSaving(true);
            const response = await fetch(`${config.API_URI}/v1/users/me/settings`, {
                method: "PUT",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify(changedSetting),
            });

            if (!response.ok) {
                throw new Error("Failed to update settings");
            }

            setAutoSaving(false);
            setLastSaved(Date.now());
        } catch (error) {
            console.error("Error auto-saving settings:", error);
            setAutoSaving(false);
            showError(dispatch, "Failed to auto-save settings.");
        }
    }, [dispatch]);

    // Effects
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${config.API_URI}/v1/users/me/settings`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch settings");
                }

                const { settings, slack } = await response.json();

                // Update all settings states with proper fallbacks
                setSettings(settings);
                setSlackSettings(slack);
                setSlackConnected(slack?.connected || false);

                // Theme color - use themeColor from backend, fallback to 'blue'
                const themeColor = settings.themeColor || 'blue';
                setSelectedThemeColor(themeColor);
                // Apply theme color to DOM
                document.documentElement.setAttribute("data-theme-color", themeColor);

                // Theme preference - use preferredTheme from backend, fallback to 'system'
                const themePreference = settings.preferredTheme || 'system';
                setSelectedTheme(themePreference);
                // Apply theme preference
                setTheme(themePreference);

                // Language - map backend language to frontend code, fallback to 'en'
                setLanguage(mapBackendLanguageToCode(settings.language));

                // Region/Timezone - use timezone from backend, fallback to 'Europe/London'
                setRegion(settings.timezone || 'Europe/London');

                // Boolean settings with proper fallbacks
                setHighContrast(settings.highContrast || false);
                setPushNotifications(settings.pushNotifications || false);
                setSlackNotifications(settings.slackNotifications || false);
                setEmailNotifications(settings.emailNotifications !== undefined ? settings.emailNotifications : true);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    // Fetch workspaces if in workspace context
    useEffect(() => {
        if (isWorkspaceSettings && workspaces.length === 0) {
            dispatch(fetchWorkspaces());
        }
    }, [dispatch, isWorkspaceSettings, workspaces.length]);

    // Set current workspace when workspaces are loaded
    useEffect(() => {
        if (isWorkspaceSettings && shortId && workspaces.length > 0) {
            const workspace = find.get(workspaces, { name: 'workspace', workspace: shortId });
            if (workspace) {
                setCurrentWorkspace(workspace);
            }
        }
    }, [isWorkspaceSettings, shortId, workspaces]);

    // Check notification permission status on mount and when it changes
    useEffect(() => {
        const checkPermission = () => {
            if ('Notification' in window) {
                setNotificationPermission(Notification.permission);
            }
        };

        checkPermission();

        // Listen for permission changes (some browsers support this)
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
                permissionStatus.onchange = () => {
                    checkPermission();
                };
            }).catch(() => {
                // Fallback for browsers that don't support permissions API
                console.log('Permissions API not supported');
            });
        }
    }, []);



    // Handlers
    const handleLanguage = (selectedOption) => {
        const newLanguageCode = selectedOption.value;
        setLanguage(newLanguageCode);
        // Convert frontend language code to backend format
        autoSave({ language: mapLanguageCodeToBackend(newLanguageCode) });
    };

    const handleRegion = (selectedOption) => {
        const newRegion = selectedOption.value;
        setRegion(newRegion);
        // Backend expects 'timezone' field, not 'region'
        autoSave({ timezone: newRegion });
    };

    const handleThemeColor = (color) => {
        document.documentElement.setAttribute("data-theme-color", color);
        setSelectedThemeColor(color);
        // Backend expects 'preferredThemeColor' and saves as 'themeColor'
        autoSave({ preferredThemeColor: color });
    };

    const handleSlackConnect = () => {
        const clientId = "8510357759874.8519400330340";
        const redirectUri = encodeURIComponent("https://zoobbe.com/slack/auth");
        const scopes = [
            "chat:write",
            "im:write",
            "users:read",
            "users:read.email"
        ].join(",");

        const state = "randomstring"; // 👉 ideally generate a random string per request for security

        window.location.href =
            `https://slack.com/oauth/v2/authorize?client_id=${clientId}` +
            `&scope=${scopes}` +
            `&redirect_uri=${redirectUri}` +
            `&state=${state}`;
    };


    const handleSlackDisconnect = async () => {
        try {
            const response = await fetch(
                `${config.API_URI}/v1/users/slack/auth/disconnect`,
                {
                    method: "POST",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to disconnect Slack");
            }

            setSlackConnected(false);
            dispatch(fetchUser());
        } catch (error) {
            console.error("Error disconnecting Slack:", error.message);
            alert(error.message);
        }
    };

    // Handle push notification permission request
    const handlePushNotificationToggle = async () => {
        if (!pushNotifications) {
            // User is trying to enable push notifications
            if (notificationPermission === 'default') {
                // Request permission
                try {
                    const permission = await Notification.requestPermission();
                    setNotificationPermission(permission);

                    if (permission === 'granted') {
                        setPushNotifications(true);
                        autoSave({ pushNotifications: true });
                        dispatch(showSnackBar({
                            message: 'Push notifications enabled successfully!',
                            type: 'success'
                        }));
                    } else {
                        dispatch(showSnackBar({
                            message: 'Push notifications permission denied. You can enable it in your browser settings.',
                            type: 'warning'
                        }));
                    }
                } catch (error) {
                    console.error('Error requesting notification permission:', error);
                    dispatch(showSnackBar({
                        message: 'Failed to request notification permission.',
                        type: 'error'
                    }));
                }
            } else if (notificationPermission === 'granted') {
                // Permission already granted, just enable
                setPushNotifications(true);
                autoSave({ pushNotifications: true });
            } else if (notificationPermission === 'denied') {
                // Permission denied, show message
                dispatch(showSnackBar({
                    message: 'Push notifications are blocked. Please enable them in your browser settings and refresh the page.',
                    type: 'warning'
                }));
            }
        } else {
            // User is disabling push notifications
            setPushNotifications(false);
            autoSave({ pushNotifications: false });
        }
    };


    return (
        <>
            <ProfileNavbar />
            <div className="settings-container">
                {/* Theme Color Section */}
                <div className="setting-row">
                    <div className="setting-label">
                        <h2>Theme color</h2>
                        <p>Choose a preferred theme for the app.</p>
                    </div>
                    <div className="theme-colors">


                        {themeColors.map(({ name, code }) => (
                            <div
                                key={name}
                                className={`color-option ${selectedThemeColor === name ? "selected" : ""}`}
                                style={{
                                    backgroundColor: code,
                                    color: selectedThemeColor === name ? code : "inherit", // Ensuring outline matches the color
                                }}
                                onClick={() => handleThemeColor(name)}
                            >
                                {selectedThemeColor === name && (
                                    <span className="material-symbols-outlined checkmark">check</span>
                                )}
                            </div>
                        ))}

                    </div>
                </div>

                {/* Appearance Section */}
                <div className="setting-row">
                    <div className="setting-label">
                        <h2>Appearance</h2>
                        <p>Choose light or dark mode, or switch your mode automatically based on your system settings.</p>
                    </div>
                    <div className="appearance-options">
                        {["light", "dark", "system"].map((mode) => (
                            <div className="theme-mode" key={mode}>
                                <div
                                    className={`appearance-option ${selectedTheme === mode ? "selected" : ""}`}
                                    onClick={() => {
                                        setSelectedTheme(mode);
                                        setTheme(mode);
                                        autoSave({ preferredTheme: mode });
                                    }}
                                >
                                    <div className={`mode-preview ${mode}`}>{selectedTheme === mode && <span className="material-symbols-outlined checkmark">check</span>}</div>
                                </div>

                                <label className="mode-label">{textTransform(mode, 'capitalize')}</label>
                            </div>

                        ))}
                    </div>
                </div>
                <div className="setting-row">
                    <div className="setting-label">
                        <h2>Contrast</h2>
                        <p>Turn on and off high contrast text and borders.</p>
                    </div>
                    <div className="toggle-container full-deactivated">
                        <label className="toggle-switch">
                            <input type="checkbox" disabled checked={highContrast} onChange={() => {
                                const newHighContrast = !highContrast;
                                setHighContrast(newHighContrast);
                                autoSave({ highContrast: newHighContrast });
                            }} />
                            <span className="slider"></span>
                        </label>
                        <span>High Contrast for increased accessibility</span>
                    </div>
                </div>

                <div className="setting-row">
                    <div className="setting-label">
                        <h2>Language & Region</h2>
                        <p>Customize your language and region.</p>
                    </div>
                    <div className="language-region-options">
                        <div className="group">
                            <h3>Language</h3>
                            <SearchSelect
                                options={languageOptions}
                                onSelect={handleLanguage}
                                placeholder="Select an option"
                                isGrouped={false}
                                defaultValue={language || 'en'}
                                isDisabled={true} // Disable region selection

                            />
                        </div>
                        <div className="group">
                            <h3>Region</h3>
                            <SearchSelect
                                options={regionOptions}
                                onSelect={handleRegion}
                                placeholder="Select an option"
                                isGrouped={false}
                                defaultValue={region || 'Europe/London'}
                                isDisabled={true} // Disable region selection
                            />
                        </div>
                    </div>
                </div>

                <div className="notification-settings">
                    <h2>Notification Preferences</h2>
                    <p>Choose how you want to stay updated.</p>

                    <div className="settings-inner-container">


                        {/* Push Notifications */}
                        <div className="setting-row">
                            <div className="setting-label">
                                <h3>
                                    <span className="material-symbols-outlined" style={{ color: "var(--brand-color)" }}>
                                        web_asset
                                    </span>
                                    Browser Notification
                                </h3>
                                <p>Turn on browser notifications to stay updated with important alerts and messages.</p>
                            </div>
                            <div className="toggle-container">
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={pushNotifications}
                                        onChange={handlePushNotificationToggle}
                                    />
                                    <span className="slider"></span>
                                </label>
                                <span>
                                    Receive notifications for updates and messages
                                    {notificationPermission === 'denied' && (
                                        <div className="permission-warning">
                                            <span className="material-symbols-outlined warning-icon">warning</span>
                                            Notifications are blocked in your browser settings
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Slack Notifications */}
                        <div className="setting-row">
                            <div className="setting-label">
                                <h3>
                                    <svg width="24" height="24" viewBox="0 0 0.48 0.48" xmlns="http://www.w3.org/2000/svg" fill="none"><g fillRule="evenodd"><path fill="#E01E5A" d="M.074.339A.044.044 0 0 0 .118.295V.251H.074a.044.044 0 0 0-.045.044.044.044 0 0 0 .044.044zM.185.251a.044.044 0 0 0-.044.044v.11c0 .024.02.044.044.044A.044.044 0 0 0 .229.406V.295A.044.044 0 0 0 .185.251" /><path fill="#36C5F0" d="M.141.074a.044.044 0 0 0 .044.044h.044V.074A.044.044 0 0 0 .185.029a.044.044 0 0 0-.044.044zm.088.11A.044.044 0 0 0 .185.141H.074a.044.044 0 0 0-.045.044c0 .024.02.044.044.044h.111A.044.044 0 0 0 .228.185z" /><path fill="#2EB67D" d="M.295.229A.044.044 0 0 0 .339.185V.074A.044.044 0 0 0 .295.029a.044.044 0 0 0-.044.044v.111c0 .024.02.044.044.044zM.406.14a.044.044 0 0 0-.044.044v.044h.044A.044.044 0 0 0 .451.185.044.044 0 0 0 .406.141z" /><path fill="#ECB22E" d="M.251.295a.044.044 0 0 0 .044.044h.11A.044.044 0 0 0 .451.295.044.044 0 0 0 .406.251H.295a.044.044 0 0 0-.044.044m.088.11A.044.044 0 0 0 .295.362H.251v.044A.044.044 0 0 0 .295.45.044.044 0 0 0 .339.406" /></g></svg>
                                    Slack Notifications
                                </h3>
                                <p>Get Zoobbe notifications directly in your Slack DMs.</p>
                            </div>
                            <div className="connect-slack-container">

                                <div className={`connect-with-slack-notifications`}>
                                    <button
                                        type="button"
                                        className={`${slackConnected ? "slack-connected" : ""}`}
                                        onClick={slackConnected ? handleSlackDisconnect : handleSlackConnect}
                                    >
                                        <span className="material-symbols-outlined">
                                            {slackConnected ? "link_off" : "open_in_new"}
                                        </span>
                                        {slackConnected ? "Disconnect Slack" : "Connect with Slack"}
                                    </button>
                                </div>


                                {slackConnected && (
                                    <div className="toggle-container">
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={slackNotifications}
                                                onChange={() => {
                                                    const newSlackNotifications = !slackNotifications;
                                                    setSlackNotifications(newSlackNotifications);
                                                    autoSave({ slackNotifications: newSlackNotifications });
                                                }}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                        <span>Notify me via Slack</span>
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* Email Notifications */}
                        <div className="setting-row">
                            <div className="setting-label">
                                <h3>
                                    <span className="material-symbols-outlined" style={{ color: "#238b5c" }}>
                                        mail
                                    </span>
                                    Email Notifications
                                </h3>
                                <p>Stay informed via email about account activity and updates.</p>
                            </div>
                            <div className="toggle-container full-deactivated">
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={emailNotifications}
                                        onChange={() => {
                                            const newEmailNotifications = !emailNotifications;
                                            setEmailNotifications(newEmailNotifications);
                                            autoSave({ emailNotifications: newEmailNotifications });
                                        }}
                                    />
                                    <span className="slider"></span>
                                </label>
                                <span>Get latest updates via email</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Auto-save Status */}
                <div className="setting-row">
                    <div className="setting-label"></div>
                    <AutoSaveStatus autoSaving={autoSaving} lastSaved={lastSaved} />
                </div>

            </div >
        </>
    );
};

export default Settings;
